import i18n, { getDateLocale } from '@/i18n';
import { Subscription } from '@haccp/api-client';

/**
 * Get CSS class for subscription status badge
 */
export function getSubscriptionStatusClass(status: Subscription['status']): string {
  switch (status) {
    case 'ACTIVE':
      return 'bg-green-50 text-green-700 border border-green-200';
    case 'PAST_DUE':
      return 'bg-yellow-50 text-yellow-700 border border-yellow-200';
    case 'CANCELED':
      return 'bg-red-50 text-red-700 border border-red-200';
    case 'LOCKED_MANUAL':
      return 'bg-orange-50 text-orange-700 border border-orange-200';
    default:
      return 'bg-neutral-50 text-neutral-700 border border-neutral-200';
  }
}

/**
 * Get human-readable subscription status
 */
export function getSubscriptionStatusText(status: Subscription['status']): string {
  return i18n.t(`adminList.subStatus.${status}`, { defaultValue: status });
}

/**
 * Format date for admin display
 */
export function formatAdminDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString(getDateLocale(i18n.language), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format date and time for admin display
 */
export function formatAdminDateTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString(getDateLocale(i18n.language), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Check if subscription is expiring soon (7 days or less)
 */
export function isSubscriptionExpiringSoon(subscription: Subscription): boolean {
  const expiresAt = new Date(subscription.currentPeriodEnd);
  const now = new Date();
  const daysUntilExpiry = (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
}

/**
 * Get days until subscription expiration
 */
export function getDaysUntilExpiry(subscription: Subscription): number {
  const expiresAt = new Date(subscription.currentPeriodEnd);
  const now = new Date();
  return Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Format currency for display
 */
export function formatCurrency(cents: number): string {
  return (cents / 100).toLocaleString(getDateLocale(i18n.language), {
    style: 'currency',
    currency: 'EUR',
  });
}
